import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Exchange } from '../types/exchange'
import { exchangeApi } from '../services/api/exchange'
import { useAuth } from './AuthContext'

interface ExchangeContextType {
  exchanges: Exchange[]
  currentExchange: Exchange | null
  setCurrentExchange: (exchange: Exchange | null) => void
  isLoading: boolean
  refreshExchanges: () => Promise<void>
  createExchange: (name: string, accountId?: string) => Promise<Exchange>
}

const ExchangeContext = createContext<ExchangeContextType>({
  exchanges: [],
  currentExchange: null,
  setCurrentExchange: () => {},
  isLoading: true,
  refreshExchanges: async () => {},
  createExchange: async () => ({ id: '', accountId: '', name: '', status: 'active', createdAt: '', updatedAt: '' })
})

export const useExchange = () => {
  const context = useContext(ExchangeContext)
  return context
}

interface ExchangeProviderProps {
  children: ReactNode
}

export const ExchangeProvider: React.FC<ExchangeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const [exchanges, setExchanges] = useState<Exchange[]>([])
  const [currentExchange, setCurrentExchangeState] = useState<Exchange | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadExchanges = async () => {
    if (!isAuthenticated || !user) {
      setExchanges([])
      setCurrentExchangeState(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const data = await exchangeApi.getExchanges()
      
      // Enrich exchanges with address from localStorage wizard state
      const enrichedExchanges = data.map(exchange => {
        try {
          const key = `exchange_wizard_v1_${exchange.id}_initial`
          const raw = localStorage.getItem(key)
          if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed?.relinquished?.address) {
              const addressParts = [
                parsed.relinquished.address,
                parsed.relinquished.city,
                parsed.relinquished.state,
                parsed.relinquished.zip
              ].filter(Boolean)
              return {
                ...exchange,
                address: addressParts.join(', ')
              }
            }
          }
        } catch {}
        return exchange
      })
      
      setExchanges(enrichedExchanges)

      // Restore current exchange from localStorage or use first exchange
      const savedExchangeId = localStorage.getItem('current_exchange_id')
      if (savedExchangeId) {
        const saved = enrichedExchanges.find(e => e.id === savedExchangeId)
        if (saved) {
          setCurrentExchangeState(saved)
        } else if (enrichedExchanges.length > 0) {
          setCurrentExchangeState(enrichedExchanges[0])
          localStorage.setItem('current_exchange_id', enrichedExchanges[0].id)
        }
      } else if (enrichedExchanges.length > 0) {
        setCurrentExchangeState(enrichedExchanges[0])
        localStorage.setItem('current_exchange_id', enrichedExchanges[0].id)
      }
    } catch (error) {
      console.error('Failed to load exchanges:', error)
      setExchanges([])
      setCurrentExchangeState(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadExchanges()
  }, [isAuthenticated, user?.id])

  useEffect(() => {
    if (!isAuthenticated) return
    const handleFocus = () => { loadExchanges() }
    const handleDemoExchangeReady = () => { loadExchanges() }
    window.addEventListener('focus', handleFocus)
    window.addEventListener('demo-exchange-ready', handleDemoExchangeReady)
    const intervalId = setInterval(() => { loadExchanges() }, 30_000)
    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('demo-exchange-ready', handleDemoExchangeReady)
      clearInterval(intervalId)
    }
  }, [isAuthenticated, user?.id])

  const setCurrentExchange = (exchange: Exchange | null) => {
    setCurrentExchangeState(exchange)
    if (exchange) {
      localStorage.setItem('current_exchange_id', exchange.id)
    } else {
      localStorage.removeItem('current_exchange_id')
    }
  }

  const createExchange = async (name: string, accountId?: string): Promise<Exchange> => {
    const newExchange = await exchangeApi.createExchange({ name, accountId })
    await loadExchanges()
    setCurrentExchange(newExchange)
    return newExchange
  }

  const value: ExchangeContextType = {
    exchanges,
    currentExchange,
    setCurrentExchange,
    isLoading,
    refreshExchanges: loadExchanges,
    createExchange,
  }

  return (
    <ExchangeContext.Provider value={value}>
      {children}
    </ExchangeContext.Provider>
  )
}

